// src/components/layouts/BranchContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BranchContext = createContext(null);

export const useBranch = () => {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    throw new Error('useBranch must be used inside <BranchProvider>');
  }
  return ctx;
};

export const BranchProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

  const [branchList, setBranchList] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);

  const [selectedBranchId, setSelectedBranchIdState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('selectedBranchId');
      if (stored) return stored;
      const user = JSON.parse(sessionStorage.getItem('user')) || {};
      return user?.branch_id ? String(user.branch_id) : '';
    } catch {
      return '';
    }
  });

  const isMainBranchAdmin = Boolean(
    JSON.parse(sessionStorage.getItem('user') || '{}')?.is_main_branch_admin
  );

  // Every page that calls this will update the SAME value everywhere
  const setSelectedBranchId = useCallback((id) => {
    setSelectedBranchIdState(id);
    sessionStorage.setItem('selectedBranchId', id);
  }, []);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user')) || {};
    if (user.organization_id) setOrganizationId(user.organization_id);
  }, []);

  const fetchBranches = useCallback(async (orgId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(
        `${apiUrl}/api/master/organization/${encodeURIComponent(orgId || organizationId)}/branches/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const data = await response.json();
      const allBranches = Array.isArray(data) ? data : data.results || [];

      const user = JSON.parse(sessionStorage.getItem('user')) || {};
      const resolvedOrgId =
        orgId ||
        user?.organization_id ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem('client'))?.id;
          } catch {
            return null;
          }
        })();

      const filtered = resolvedOrgId
        ? allBranches.filter((b) => String(b.organization) === String(resolvedOrgId))
        : allBranches;

      setBranchList(filtered);

      // Only default it once — don't override a branch the user already picked
      if (user?.branch_id && !sessionStorage.getItem('selectedBranchId')) {
        setSelectedBranchId(String(user.branch_id));
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  }, [apiUrl, organizationId, setSelectedBranchId]);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user')) || {};
    fetchBranches(user.organization_id || organizationId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const value = { selectedBranchId, setSelectedBranchId, branchList, isMainBranchAdmin, organizationId };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};